import {
  map,
  set,
  pathOr,
  lensPath,
  split,
  concat,
  equals,
  filter,
  mergeDeepLeft,
  dropLast,
  type,
  mergeDeepRight,
  head,
  omit,
  when,
  includes
} from 'ramda';
import { compose } from 'recompose';
import { isNotEmpty } from 'src/utilities/hasValue';
import {
  LENDSMART_SH_API_RESOURCE_WRITE,
  LENDSMART_SH_PRE_SIGN_FIELD,
} from 'src/features/core/Intents/intentConstants';
import { getTypeMeta, mkObjectForLoanRole } from 'src/services/objects/build';
import { monthlyIncomeSchema } from 'src/services/objects/monthlyIncomeSchema';
import { useSessionHook } from 'src/hooks/useSessionHook';
import { mkAnswer } from '../../Engine/question';
import {
  OBJECT_META,
  LABELS,
  META_DATA,
  DEFAULT,
  OWNER_REFERENCES,
  API_RESOURCE_BUSINESS
} from 'src/services/constants';
import { getNamespaceName } from 'src/services/objects/namespaces';
import { buildLabel } from 'src/utilities/naming';
import { ISessionStore, SessionStateStore } from '../../Configs/apiResouces';
import { ROLE_COBORROWER } from 'src/constants';
import { currentAppSession } from 'src/utilities/storage';


/* mkUpdateAPIFields
 * This is a onetime call after the password flow data is captured.
 *
 * Get all the answer from workflow intent and built an api structure
 * -----
 *
 * Input
 * Storage: Saved answerd from password less flow is the termed as  workflow intent.
 *
 * eg: Here is a single intent
 *
 * [
  {
    "answer": "32432",
    "question_id": "GTKQ1.a",
    "order_id": "26",
    "is_hide_this_intent": false,
    "is_mask_this_intent_from_view": false,
    "metadata": {
      "lendsmart_sh/api_resource_read": "lendingTerm",
      "lendsmart_sh/api_resource_write": "lendingTerm",
      "lendsmart_sh/field": "terms/amount"
    },
    "key_field": "loan_amount",
    "created_at": "2022-11-17T13:32:33.411+00:00",
    "updated_at": "2022-11-17T13:32:33.411+00:00"
  }
]
 *
 * Output:  Updated api resource
 * eg:
 lendingTerm:{
  terms:{
    amount:32432
  }
}
 * Example 1:
 *
 */


const setFieldsValue = (question: any) => {
  const savedInAPI = pathOr({}, ['metadata'], question);
  //
  const answer = mkAnswer(question);
  // Convert the index `former/address/0` is split to  ['former','0', 'address']
  const locationToSave = splitTheSavedDataPath(
    pathOr('', [LENDSMART_SH_API_RESOURCE_WRITE], savedInAPI),
    pathOr('', [LENDSMART_SH_PRE_SIGN_FIELD], savedInAPI)
  );

  // Convert the index to numberic  ['former','0', 'address'] to
  // ['former', 0 , 'address']
  const location = _getLocationToSaveWithIndexAlterations(locationToSave);

  // Set the answer example { borrower : profile : {address: "test" }}
  return set(lensPath(location), answer);
};

export const mkUpdateApiFields = () => {
  // mk the teaser storage. When we start its an empty array
  const { workFlow } = useSessionHook();
  const updIntents = filter(
    (item: any) => isNotEmpty(pathOr('', ['answer'], item)),
    pathOr([], ['intents'], workFlow)
  )

  const updQuestions = map(setFieldsValue, updIntents);

  // Convert the updatesQuestions array [...] to an object
  const result = compose(...updQuestions)({});
  // To circumvent the problem where the
  // profile : { name, address } save wipes out name when trying to save the
  // address, we do an another round of update.
  //
  const updResult = updateRelated(result, updIntents);
  return updResult;
};

//This method provides ability to update the same resources repeatedly.
const updateRelated = (data: any, displayers: any) => {
  const setFieldsValue = (field: any) => {
    // What was answered
    const value = mkAnswer(field);
    const metaData = pathOr({}, [META_DATA], field);
    /// is this an object update?
    const isObject = pathOr('', ['is_object'], metaData);
    /// find the exact path input: profile/obj, output: profile
    const path = dropLast(
      1,
      split('/', pathOr('', [LENDSMART_SH_PRE_SIGN_FIELD], metaData))
    );
    ///build the path to save
    // input: lendsmart_api_write: eg: borrower
    //        path: profile
    // output:
    // {
    // borrower/profile:{  obj: "", {name: "TEST"}}
    // }
    const apiResourceData = pathOr(
      {},
      mkResourcePath(
        pathOr('', [LENDSMART_SH_API_RESOURCE_WRITE], metaData),
        path
      ),
      data
    );
    // output: {
    //         borrower/profile:{  {name: "TEST"}}
    //         }
    delete apiResourceData['obj'];
    // If its an object, merge with the old data.
    // output:
    // {
    //  borrower/profile:{  {name: "TEST"} , {dob: "01012000"}
    // }
    const updValue = equals(type(value), 'Object')
      ? mergeDeepLeft(value, apiResourceData)
      : value;

    return (
      isObject &&
      set(
        lensPath(
          mkResourcePath(
            pathOr('', [LENDSMART_SH_API_RESOURCE_WRITE], metaData),
            path
          )
        ),
        updValue
      )
    );
  };

  const updFields = filter((item: any) => item, map(setFieldsValue, displayers));

  return compose(...updFields)(data);
};

const updateOwnerReference = (data: any) => {
  const { resources, accountId } = data;
  const { loanRole } = useSessionHook();
  const ownerReference = pathOr(
    [],
    [OWNER_REFERENCES],
    mkObjectForLoanRole(DEFAULT, buildLabel(loanRole))
  );
  return map((emp) => {
    return compose(
      set(lensPath([OBJECT_META, 'account']), accountId),
      set(lensPath([OBJECT_META, OWNER_REFERENCES]), ownerReference)
    )(omit(['id'])(emp));
  }, resources);
};

/**
mkLoanAppPayload is used to convert the workflow intent answers into
loan app resources like borrowers, employments, monthlyincomes
also form the resources to support as variable naming to create or update using
graphql queries
TODO: have to make loan app with core api supported spec model instead of some random names
*/
export const mkLoanAppPayload = (
  userData: any,
  resourceDetails: any,
  workflowIntents: any,
  specEntities: string[]
) => {
  const { productType, loanRole } = useSessionHook();
  const { loanRoleAccountId, loanAppId, loanRoleId } = currentAppSession()
  const apiResource: ISessionStore = new SessionStateStore().getStateFromStore();
  const { lendingTerm, property } = apiResource;

  const businessBySession = pathOr([], ['business'], apiResource);
  const declarationBySession = pathOr([], ['declaration'], apiResource);
  const loanDetails = mkUpdateApiFields();
  const object_meta = mkObjectForLoanRole(DEFAULT, buildLabel(loanRole));
  const mutateProperty = isNotEmpty(productType)
    ? compose(
      set(lensPath([OBJECT_META, LABELS, 'property_loan_type']), productType)
    )(pathOr({}, ['property'], loanDetails))
    : pathOr({}, ['property'], loanDetails);
  const loanDetailPropertyResource = mergeDeepLeft(mutateProperty, { object_meta });

  const borrower = setResourceDetail(mergeDeepLeft(pathOr({}, ['borrower'], loanDetails), {
    object_meta
  }), { kind: "Borrower", id: pathOr("", ["borrower", "id"], resourceDetails) });
  const lendingTermResource = setResourceDetail(mkPayLoadFromReducer({ apiResource: lendingTerm, loanDetailResource: pathOr({}, ['lendingTerms'], loanDetails), object_meta }), { kind: "LendingTerm", id: pathOr("", ["lendingTerms", "id"], resourceDetails) })
  const propertyResource = setResourceDetail(mkPayLoadFromReducer({ apiResource: property, loanDetailResource: loanDetailPropertyResource, object_meta }), { kind: "Property", id: pathOr("", ["property", "id"], resourceDetails) })

  const liabilities = setResourceDetail(mergeDeepLeft(pathOr({}, ['liabilities'], loanDetails), {
    object_meta,
  }), { kind: "CreditReport", id: pathOr("", ["liabilities", "id"], resourceDetails) });
  // const realestateAsset = mergeDeepLeft(pathOr({}, ['realestateAsset'], loanDetails), {
  //   object_meta,
  // });
  const declaration = mergeDeepLeft(omit(['id'])(declarationBySession), {
    object_meta,
  });
  const updDeclaration = setResourceDetail(declaration, { kind: "Property", id: pathOr("", ["declaration", "id"], resourceDetails) });

  const business = setResourceDetail(mergeDeepLeft(omit(['id'])(businessBySession), {
    object_meta,
  }), { kind: "Business", id: pathOr("", ["business", "id"], resourceDetails) });


  const workflowIntent = {
    data: compose(
      set(lensPath(['intents']), workflowIntents),
      set(lensPath([OBJECT_META]), object_meta),
      set(lensPath(['type_meta']), getTypeMeta('WorkFlowIntents')),
      set(
        lensPath(['created_at']),
        pathOr('', ['created_at'], head(workflowIntents))
      )
    )(userData),
  };

  const objectMeta = {
    owner_references: [
      {
        uid: loanAppId,
      },
      {
        uid: loanRoleId,
      },
    ],
  };

  const userAccounts = mergeDeepLeft(pathOr({}, ['accounts'], loanDetails), pathOr({}, ['account'], resourceDetails,
  ))

  const mortgageResources = getMortgageResources({ 
    borrower, 
    lendingTerm: lendingTermResource, 
    liabilities, 
    updDeclaration, 
    property: propertyResource, 
    objectMeta, 
    loanAppId, 
    accounts: userAccounts, 
    loanRoleAccountId, 
    loanRole 
  })

  const notMortgageResources = {
    business: business,
    objectMeta: objectMeta,
    loanAppId: loanAppId,
    property: propertyResource
  }
  
  const loanApp = includes(API_RESOURCE_BUSINESS, specEntities) ? notMortgageResources : mortgageResources;
  const employments = mkResourceDetails({ loanRoleAccountId, apiResource: pathOr([], ['employments'], apiResource) });
  const collaterals = mkResourceDetails({ loanRoleAccountId, apiResource: pathOr([], ['collateral'], apiResource) });
  const monthlyIncomes = mkMonthlyIncomeDetails();
  //TO-DO: currently we support only single assets on presignsaver we need to extend the support of multiple assets in future
  const assets = mkAssetDetails(pathOr({}, ['assets'], loanDetails));
  return { loanApp, workflowIntent, employments, monthlyIncomes, assets, collaterals };
};

/**
 * 
 * @param resource api resource
 * @param data  object contains kind and id
 * @returns modified resource with id and typemeta
 */
const setResourceDetail = (resource: any, data: any) => {
  const { id = "", kind = "" } = data;
  return compose(
    when(
      () => isNotEmpty(id),
      set(lensPath(["id"]), id)
    ),
    set(lensPath(["type_meta"]), getTypeMeta(kind))
  )(resource)
}




const getMortgageResources = (dataBag: any) => {
  const { borrower, lendingTerm, liabilities, updDeclaration, property, objectMeta, accounts, loanAppId, loanRole } = dataBag;
  const isCoBorrower = equals(loanRole, ROLE_COBORROWER)
  const mortgageResources = {
    loanAppId: loanAppId,
    objectMeta: objectMeta,
    account: accounts,
    borrower: borrower,
    property: property,
    lendingTerms: lendingTerm,
    liabilities: liabilities,
    declaration: updDeclaration,
    // realestateAsset:realestateAsset
  }
  return isCoBorrower ? omit(['property', 'loanAppId'])(mortgageResources) : mortgageResources
}

/**
 * It will make payLoad from workflow and from reducer
 * @param data contains loandetails converted from apiresource workflow ,objectmeta to be updated for the current resource and apiResource from reducer data
 * @returns payload formed from reducer and workflow
 */
const mkPayLoadFromReducer = (data: any) => {
  const { object_meta, apiResource, loanDetailResource } = data;
  return mergeDeepLeft(
    omit(['id'])(apiResource),
    mergeDeepLeft(loanDetailResource, {
      object_meta,
    })
  );
};




export const mkLoanAppFromSessionWorkflowIntent = () => {
  const { workFlow } = useSessionHook();
  const apiResource = new SessionStateStore().getStateFromStore();
  const business = pathOr([], ['business'], apiResource);
  const { loanApp, workflowIntent, employments, monthlyIncomes } =
    mkLoanAppPayload(
      {},
      {},
      workFlow,
      []
    );
  return {
    object_meta: {
      namespace: getNamespaceName(),
    },
    spec: {
      user_accounts: [pathOr({}, ['account'], loanApp)],
      borrowers: [pathOr({}, ['borrower'], loanApp)],
      properties: pathOr({}, ['property'], loanApp),
      lending_terms: pathOr({}, ['lendingTerms'], loanApp),
      liabilities: [pathOr({}, ['liabilities'], loanApp)],
      declaration: [pathOr({}, ['declaration'], loanApp)],
      employments,
      monthly_incomes: monthlyIncomes,
      workflow_intents: workflowIntent,
      business: business
    },
  };
};

/**
mkResourceDetails is used to make provided resource payload from reducer and set the ownerreference
*/
const mkResourceDetails = (data: any) => {
  const {
    loanRoleAccountId,
    apiResource
  } = data;

  const empDetails = updateOwnerReference({
    resources: apiResource,
    accountId: loanRoleAccountId,
  });

  return empDetails
};

// Creating other monthly income payload from monthlyIncomes session
const mkMonthlyIncomeDetails = () => {
  const { loanRole } = useSessionHook();
  const objectMeta = mkObjectForLoanRole(DEFAULT, loanRole);
  const apiResource = new SessionStateStore().getStateFromStore();
  const monthlyIncomes = pathOr([], ['monthlyIncomes'], apiResource);

  const incomes = map(
    (item: any) =>
      mergeDeepRight(mergeDeepRight(monthlyIncomeSchema(item), omit(['id'])(item)), {
        object_meta: objectMeta,
      }),
    monthlyIncomes
  );

  return incomes;
};


const mkAssetDetails = (assets: any) => {
  const { loanRole } = useSessionHook();
  const objectMeta = mkObjectForLoanRole(DEFAULT, loanRole);
  return isNotEmpty(assets) ? mergeDeepRight(omit(['id'])(assets), {
    object_meta: objectMeta,
  }) : null



};

/**
 * this method used for conver string to array
 * input ["key1", "0", "key2"]
 * output ["key1", 0, "key2"]
 */
export const _getLocationToSaveWithIndexAlterations = (locationToSave: any) => {
  return map((location: any) => {
    if (!isNaN(Number(location))) {
      return Number(location);
    }
    return location;
  }, locationToSave);
};
//make save path location
const mkResourcePath = (resource: any, data: any) => {
  const location = _getLocationToSaveWithIndexAlterations(
    concat([resource], data)
  );
  return location;
};
const splitTheSavedDataPath = (resource: any, data: any) => {
  const result = concat([resource], split('/', data));
  return result;
};
